import { useId } from "react";
import { useFetcher } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { requireSessionWith } from "@/lib/guards";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { DIVISIONS } from "@/lib/results";
import { browserTimeZone, timeZoneOptions } from "@/lib/time-zones";
import { SegmentedControl } from "@/components/form";
import { AppearanceControl } from "@/components/theme";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { AccountPage, type ActionResult } from "@/routes/account-section";

/**
 * The private defaults, a page each (ADR 0068), saved the moment they are
 * tapped (decided with Tiago 12 October), and the appearance, which is this
 * device's alone and never sent (0059). Neither default is required, and
 * "not chosen" is a state of its own. There are no units to choose since 19
 * October: the app is metric only.
 */

export async function defaultsLoader({ request }: { request: Request }) {
  await requireSessionWith(request, () => read(queries.profile()));
  return null;
}

/**
 * The division as segments, sent by its own fetcher when a segment is
 * tapped. While it is on its way, and while the page reads it back, the
 * segment tapped stays lit; a refusal puts back what was saved and says why
 * beneath it. Clear goes back to none lit.
 */
export function AccountDivision() {
  const { data: profile } = useSuspenseQuery(queries.profile());
  const fetcher = useFetcher<ActionResult>();
  const sending = fetcher.formData?.get("division");
  const value = typeof sending === "string" ? sending : (profile.division ?? "");
  const save = (next: string) => fetcher.submit({ intent: "profile", division: next }, { method: "post" });

  return (
    <AccountPage title="Division">
      <div className="space-y-2">
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <SegmentedControl label="Division" labelHidden fill name="division" options={[...DIVISIONS]} value={value} onChange={save} />
          </div>
          {value !== "" && (
            <Button
              type="button"
              variant="outline"
              aria-label="Clear division"
              disabled={fetcher.state !== "idle"}
              onClick={() => save("")}
            >
              Clear
            </Button>
          )}
        </div>
        {fetcher.data?.error && <FieldError>{fetcher.data.error}</FieldError>}
      </div>
    </AccountPage>
  );
}

/** The time zone, a long list, saved the moment another is chosen: the phone's own picker. */
export function AccountTimeZone() {
  const { data: profile } = useSuspenseQuery(queries.profile());
  const fetcher = useFetcher<ActionResult>();
  const sending = fetcher.formData?.get("timeZone");
  const value = typeof sending === "string" ? sending : (profile.timeZone ?? "");
  const here = browserTimeZone();
  const id = useId();

  return (
    <AccountPage title="Time zone">
      <Field>
        <FieldLabel htmlFor={id} className="sr-only">
          Time zone
        </FieldLabel>
        <NativeSelect
          id={id}
          name="timeZone"
          value={value}
          onChange={(event) => fetcher.submit({ intent: "profile", timeZone: event.currentTarget.value }, { method: "post" })}
          className="w-full"
        >
          <NativeSelectOption value="">This device's — {here}</NativeSelectOption>
          {timeZoneOptions(value).map((zone) => (
            <NativeSelectOption key={zone} value={zone}>
              {zone}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        {fetcher.data?.error && <FieldError>{fetcher.data.error}</FieldError>}
      </Field>
    </AccountPage>
  );
}

/** Light, Dark or Device: taking effect as it is tapped, so no toast. */
export function AccountAppearance() {
  return (
    <AccountPage title="Appearance">
      <AppearanceControl />
    </AccountPage>
  );
}
